'use client';

/**
 * REWRITTEN TO BE SSR-SAFE
 * This version uses dynamic imports to ensure the server never
 * touches the worker-based GIF libraries.
 */

const rotateGifToPortrait = async (
    file: File,
    resolve: (f: File) => void,
    reject: (err: any) => void
): Promise<void> => {
    // 1. Check if window exists (safety check)
    if (typeof window === 'undefined') return;

    const reader = new FileReader();
    reader.onload = async () => {
        try {
            // 2. Dynamically import libraries only on the client
            const [GIFModule, gifuct] = await Promise.all([import('gif.js'), import('gifuct-js')]);

            // Some libraries require accessing .default depending on your build setup
            const GIF = (GIFModule as any).default || GIFModule;
            const { parseGIF, decompressFrames } = gifuct;

            // Parse animated GIF
            const gif = parseGIF(reader.result as ArrayBuffer);
            const frames = decompressFrames(gif, true);

            const { dims } = frames[0];
            const width = dims.width;
            const height = dims.height;

            // If already portrait, return as-is
            if (height >= width) {
                resolve(file);
                return;
            }

            const gifEncoder = new GIF({
                workers: 4,
                quality: 10,
                width: height,
                height: width,
                workerScript: '/js/gif.worker.js',
            });

            // Canvas to compose frames respecting disposal types
            const compositeCanvas = document.createElement('canvas');
            compositeCanvas.width = width;
            compositeCanvas.height = height;
            const compositeCtx = compositeCanvas.getContext('2d');
            if (!compositeCtx) throw new Error('Could not get composite canvas context');

            // Save previous canvas for disposalType = 3
            let previousCanvasImage: ImageData | null = null;

            frames.forEach((frame, index) => {
                // Save current canvas state if next frame may need restore
                if (frame.disposalType === 3 && compositeCtx) {
                    previousCanvasImage = compositeCtx.getImageData(0, 0, width, height);
                }

                // Handle disposal of previous frame
                if (index > 0) {
                    const prevFrame = frames[index - 1];
                    if (prevFrame.disposalType === 2) {
                        compositeCtx.clearRect(
                            prevFrame.dims.left,
                            prevFrame.dims.top,
                            prevFrame.dims.width,
                            prevFrame.dims.height
                        );
                    } else if (prevFrame.disposalType === 3 && previousCanvasImage) {
                        compositeCtx.putImageData(previousCanvasImage, 0, 0);
                    }
                }

                // Draw current frame patch using new ImageData via a temporary canvas for correct alpha blending
                const imageData = new ImageData(
                    new Uint8ClampedArray(frame.patch),
                    frame.dims.width,
                    frame.dims.height
                );

                // Create a temporary canvas for the patch
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = frame.dims.width;
                tempCanvas.height = frame.dims.height;
                const tempCtx = tempCanvas.getContext('2d');
                if (tempCtx) {
                    tempCtx.putImageData(imageData, 0, 0);
                    compositeCtx.drawImage(tempCanvas, frame.dims.left, frame.dims.top);
                }

                // Create a temporary rotated canvas
                const rotatedCanvas = document.createElement('canvas');
                rotatedCanvas.width = height;
                rotatedCanvas.height = width;
                const rotatedCtx = rotatedCanvas.getContext('2d');
                if (!rotatedCtx) return;

                // Clear the rotated canvas before drawing
                rotatedCtx.clearRect(0, 0, rotatedCanvas.width, rotatedCanvas.height);

                // Rotate 90 degrees clockwise
                rotatedCtx.translate(height / 2, width / 2);
                rotatedCtx.rotate((90 * Math.PI) / 180);
                rotatedCtx.drawImage(compositeCanvas, -width / 2, -height / 2);

                // Add the rotated frame to the new GIF encoder with the correct frame delay
                gifEncoder.addFrame(rotatedCtx, { copy: true, delay: frame.delay });
            });

            // Wrap the blob into a new File object
            gifEncoder.on('finished', (blob: Blob) => {
                const rotatedFile = new File([blob], file.name, { type: file.type });
                resolve(rotatedFile);
            });

            // Resolve the encoding
            gifEncoder.render();
        } catch (err) {
            reject(err);
        }
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
};

const rotateImageFileToPortrait = (file: File, resolve: (f: File) => void, reject: (err: any) => void): void => {
    if (typeof window === 'undefined') return;

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
        const width = img.width;
        const height = img.height;

        // If already portrait, return as-is
        if (height >= width) {
            URL.revokeObjectURL(url);
            resolve(file);
            return;
        }

        // Create canvas with swapped dimensions
        const canvas = document.createElement('canvas');
        canvas.width = height;
        canvas.height = width;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
        }

        // Rotate 90 degrees clockwise
        ctx.translate(height / 2, width / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -width / 2, -height / 2);

        // Convert the canvas to a blob and warp it as a new File
        canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
                reject(new Error('Canvas conversion failed'));
                return;
            }
            const rotatedFile = new File([blob], file.name, { type: file.type });
            resolve(rotatedFile);
        }, file.type);
    };

    img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
    };

    img.src = url;
};

export const rotateImageToPortrait = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
        // Final safety check for SSR
        if (typeof window === 'undefined') {
            return resolve(file);
        }

        if (file.type === 'image/gif') {
            return rotateGifToPortrait(file, resolve, reject);
        }
        return rotateImageFileToPortrait(file, resolve, reject);
    });
};
